import React from "react";
import { motion } from "framer-motion";
import { Phone, Crown, Shield, BookOpen, Wallet } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHeader from "../components/SectionHeader";
import { useLanguage } from "../contexts/LanguageContext";
import { IMAGES, LEADERSHIP } from "../data/site";

function initials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function PersonCard({ person, roleLabel, accent = "green", compact = false, testId }) {
  const accentBg =
    accent === "terracotta" ? "bg-[#B5651D]" : accent === "dark" ? "bg-[#0A0A0A]" : "bg-[#0B5D1E]";
  const accentRing =
    accent === "terracotta" ? "ring-[#B5651D]/20" : accent === "dark" ? "ring-black/15" : "ring-[#0B5D1E]/20";
  const roleColor =
    accent === "terracotta" ? "text-[#B5651D]" : "text-[#0B5D1E]";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5 }}
      className={`bg-white rounded-3xl border border-black/5 shadow-sm hover:shadow-xl transition-all ${
        compact ? "p-5" : "p-7"
      } text-center`}
      data-testid={testId}
    >
      <div
        className={`mx-auto ${compact ? "h-16 w-16 text-xl" : "h-20 w-20 text-2xl"} rounded-full ${accentBg} text-white flex items-center justify-center font-heading font-bold shadow-lg ring-4 ${accentRing}`}
      >
        {initials(person.name)}
      </div>
      <p className={`mt-4 text-xs tracking-[0.2em] font-bold uppercase ${roleColor}`}>
        {roleLabel}
      </p>
      <h4 className={`mt-1 font-heading font-bold ${compact ? "text-base" : "text-lg"} text-neutral-900`}>
        {person.name}
      </h4>
      {person.phone && (
        <a
          href={`tel:${person.phone.replace(/[^+\d]/g, "")}`}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-neutral-600 hover:text-[#0B5D1E]"
          data-testid={`phone-${testId}`}
        >
          <Phone size={12} />
          {person.phone}
        </a>
      )}
    </motion.div>
  );
}

export default function Leadership() {
  const { t } = useLanguage();

  return (
    <div data-testid="leadership-page">
      <PageHero
        eyebrow={t.leadership.eyebrow}
        title={t.leadership.title}
        subtitle={t.leadership.subtitle}
        image={IMAGES.community}
      />

      {/* ORG CHART */}
      <section className="py-20 bg-gradient-to-b from-[#0B5D1E]/[0.04] to-transparent">
        <div className="gk-container">
          <SectionHeader
            eyebrow={t.leadership.chartTitle}
            title={t.leadership.title}
            align="center"
          />

          <div className="mt-16 max-w-5xl mx-auto" data-testid="org-chart">
            {/* Tier 1 — President */}
            <div className="flex justify-center">
              <div className="w-full max-w-xs">
                <PersonCard
                  person={LEADERSHIP.president}
                  roleLabel={t.leadership.roles.president}
                  accent="green"
                  testId="org-president"
                />
              </div>
            </div>

            {/* Connector */}
            <div className="flex justify-center">
              <div className="w-px h-10 bg-[#0B5D1E]/30" />
            </div>

            {/* Tier 2 — VP/Secretary + Event Coordinator */}
            <div className="relative">
              <div className="absolute left-1/2 -top-1 w-[60%] md:w-[50%] h-px bg-[#0B5D1E]/30 -translate-x-1/2 hidden sm:block" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <div className="relative">
                  <div className="absolute left-1/2 -top-10 w-px h-10 bg-[#0B5D1E]/30 hidden sm:block" />
                  <PersonCard
                    person={LEADERSHIP.vp}
                    roleLabel={t.leadership.roles.vpSecretary}
                    accent="terracotta"
                    testId="org-vp"
                  />
                </div>
                <div className="relative">
                  <div className="absolute left-1/2 -top-10 w-px h-10 bg-[#0B5D1E]/30 hidden sm:block" />
                  <PersonCard
                    person={LEADERSHIP.eventCoordinator}
                    roleLabel={t.leadership.roles.eventCoordinator}
                    accent="dark"
                    testId="org-event-coordinator"
                  />
                </div>
              </div>
            </div>

            {/* Connector */}
            <div className="flex justify-center mt-4">
              <div className="w-px h-10 bg-[#0B5D1E]/30" />
            </div>

            {/* Tier 3 — Advisors + Treasurers */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-3xl border border-black/5 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-10 w-10 rounded-2xl bg-[#0B5D1E]/10 text-[#0B5D1E] flex items-center justify-center">
                    <BookOpen size={18} />
                  </div>
                  <h3 className="font-heading font-bold text-lg">{t.leadership.advisoryTitle}</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {LEADERSHIP.advisors.map((a, i) => (
                    <PersonCard
                      key={a.name}
                      person={a}
                      roleLabel={t.leadership.roles.advisor}
                      accent="green"
                      compact
                      testId={`org-advisor-${i}`}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-black/5 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-10 w-10 rounded-2xl bg-[#B5651D]/10 text-[#B5651D] flex items-center justify-center">
                    <Wallet size={18} />
                  </div>
                  <h3 className="font-heading font-bold text-lg">{t.leadership.treasurersTitle}</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {LEADERSHIP.treasurers.map((tr, i) => (
                    <PersonCard
                      key={tr.name}
                      person={tr}
                      roleLabel={t.leadership.roles.treasurer}
                      accent="terracotta"
                      compact
                      testId={`org-treasurer-${i}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
